import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Trash2, ToggleLeft, ToggleRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CouponsTab({ adminToken }) {
  const getHeaders = () => ({ Authorization: `Bearer ${adminToken}` });
  const [coupons, setCoupons] = useState([]);
  const [form, setForm] = useState({ code: "", discount_type: "percentage", discount_value: "", max_uses: "", expires_at: "" });

  const fetchCoupons = useCallback(async () => {
    try { const res = await axios.get(`${API}/admin/coupons`, { headers: { Authorization: `Bearer ${adminToken}` } }); setCoupons(res.data.coupons || []); }
    catch (err) { if (err.response?.status === 401) window.location.reload(); }
  }, [adminToken]);

  useEffect(() => { fetchCoupons(); }, [fetchCoupons]);

  const handleCreate = async () => {
    if (!form.code.trim()) { toast.error("Coupon code is required"); return; }
    try {
      await axios.post(`${API}/admin/coupons`, {
        code: form.code.trim(), discount_type: form.discount_type,
        discount_value: form.discount_type === "free" ? 100 : Number(form.discount_value),
        max_uses: form.max_uses ? Number(form.max_uses) : 0,
        expires_at: form.expires_at || null,
      }, { headers: getHeaders() });
      toast.success("Coupon created");
      setForm({ code: "", discount_type: "percentage", discount_value: "", max_uses: "", expires_at: "" });
      fetchCoupons();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleToggle = async (code, currentActive) => {
    try { await axios.put(`${API}/admin/coupons/${code}`, { active: !currentActive }, { headers: getHeaders() }); toast.success(`Coupon ${!currentActive ? "activated" : "deactivated"}`); fetchCoupons(); }
    catch { toast.error("Failed"); }
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`Delete coupon "${code}"?`)) return;
    try { await axios.delete(`${API}/admin/coupons/${code}`, { headers: getHeaders() }); toast.success("Coupon deleted"); fetchCoupons(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6">
      <Card className="bg-white border-[#E5E7EB]">
        <CardHeader><CardTitle className="text-lg text-[#111827] flex items-center gap-2"><Plus className="w-5 h-5 text-[#3B5BDB]" />Create Coupon</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div><label className="text-xs font-medium text-[#6B7280] mb-1 block">Code *</label><Input value={form.code} onChange={(e) => setForm(f => ({...f, code: e.target.value.toUpperCase()}))} placeholder="SAVE20" className="bg-white border-[#E5E7EB] uppercase" data-testid="coupon-form-code" /></div>
            <div><label className="text-xs font-medium text-[#6B7280] mb-1 block">Type</label>
              <Select value={form.discount_type} onValueChange={(v) => setForm(f => ({...f, discount_type: v}))}>
                <SelectTrigger className="bg-white border-[#E5E7EB]"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="percentage">Percentage (%)</SelectItem><SelectItem value="fixed">Fixed ($)</SelectItem><SelectItem value="free">100% Free</SelectItem></SelectContent>
              </Select>
            </div>
            <div><label className="text-xs font-medium text-[#6B7280] mb-1 block">Value</label><Input type="number" value={form.discount_value} onChange={(e) => setForm(f => ({...f, discount_value: e.target.value}))} disabled={form.discount_type === "free"} className="bg-white border-[#E5E7EB]" data-testid="coupon-form-value" /></div>
            <div><label className="text-xs font-medium text-[#6B7280] mb-1 block">Max Uses (0=unlimited)</label><Input type="number" value={form.max_uses} onChange={(e) => setForm(f => ({...f, max_uses: e.target.value}))} className="bg-white border-[#E5E7EB]" data-testid="coupon-form-max-uses" /></div>
            <div className="flex items-end"><Button onClick={handleCreate} className="w-full bg-[#3B5BDB] hover:bg-[#2F4AC7] text-white" data-testid="coupon-form-submit"><Plus className="w-4 h-4 mr-1" />Create</Button></div>
          </div>
        </CardContent>
      </Card>
      <Card className="bg-white border-[#E5E7EB]">
        <CardHeader><CardTitle className="text-lg text-[#111827]">Coupons ({coupons.length})</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="border-[#E5E7EB] bg-[#F9FAFB]">
                <TableHead className="text-[#6B7280]">Code</TableHead>
                <TableHead className="text-[#6B7280]">Discount</TableHead>
                <TableHead className="text-center text-[#6B7280]">Uses</TableHead>
                <TableHead className="text-center text-[#6B7280]">Status</TableHead>
                <TableHead className="text-right text-[#6B7280]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {coupons.length > 0 ? coupons.map(c => (
                <TableRow key={c.code} className="border-[#E5E7EB]">
                  <TableCell><span className="font-mono font-bold text-sm bg-slate-100 px-2 py-1 rounded">{c.code}</span></TableCell>
                  <TableCell className="text-sm text-[#6B7280]">{c.discount_type === "free" ? "100% Free" : c.discount_type === "percentage" ? `${c.discount_value}%` : `$${c.discount_value}`}</TableCell>
                  <TableCell className="text-center text-sm text-[#6B7280]">{c.used_count}{c.max_uses ? ` / ${c.max_uses}` : " / \u221e"}</TableCell>
                  <TableCell className="text-center"><Badge className={c.active ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"}>{c.active ? "Active" : "Inactive"}</Badge></TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => handleToggle(c.code, c.active)}>{c.active ? <ToggleRight className="w-5 h-5 text-emerald-600" /> : <ToggleLeft className="w-5 h-5 text-[#9CA3AF]" />}</Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(c.code)} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></Button>
                  </TableCell>
                </TableRow>
              )) : <TableRow><TableCell colSpan={5} className="text-center py-8 text-[#9CA3AF]">No coupons yet</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
